import React, { useEffect, useState, useRef } from 'react';
import { Plus, Search, X, Trash2, Eye } from 'lucide-react';
import { Modal, Button, FormField, Input, Select, PageHeader } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import ConfirmModal from '../../components/ConfirmModal';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { saleReturnService, saleService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

export default function SaleReturnsPage() {
  const [data, setData]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  // New Return modal
  const [formOpen, setFormOpen]   = useState(false);
  const [saleSearch, setSaleSearch] = useState('');
  const [saleResults, setSaleResults] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [returnItems, setReturnItems]   = useState([]);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [reason, setReason]             = useState('');
  const [notes, setNotes]               = useState('');
  const [saving, setSaving]             = useState(false);

  // View modal
  const [viewItem, setViewItem] = useState(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => { fetchData(); }, [page, search]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await saleReturnService.getAll({ page, limit:20, search });
      setData(r.data.data || []);
      setTotal(r.data.pagination?.total || 0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const searchSales = async (q) => {
    if (!q || q.length < 2) { setSaleResults([]); return; }
    try {
      const r = await saleService.getAll({ search: q, limit: 20 });
      setSaleResults(r.data.data || []);
    } catch {}
  };

  const selectSale = (sale) => {
    setSelectedSale(sale);
    setSaleResults([]);
    setSaleSearch(sale.invoiceNumber);
    // load all items with qty=0 initially
    setReturnItems(sale.items.map(item => ({
      medicine:     item.medicine?._id || item.medicine,
      medicineName: item.medicineName || item.medicine?.commercialName,
      maxQty:       item.quantity,
      unit:         item.unit,
      unitPrice:    item.unitPrice,
      quantity:     0,
      refundAmount: '',   // مبلغ دلخواه برگشتی برای این آیتم؛ خالی = قیمت فروش × تعداد
    })));
  };

  const updateQty = (idx, val) => {
    setReturnItems(prev => prev.map((it, i) => i===idx ? { ...it, quantity: Math.min(Number(val), it.maxQty), refundAmount: '' } : it));
  };

  // مبلغ برگشتی هر آیتم: اگر کاربر مبلغ دلخواه وارد کرده همان، وگرنه قیمت × تعداد
  const lineRefund = (i) => (i.refundAmount !== '' && i.refundAmount != null)
    ? (Number(i.refundAmount) || 0)
    : (Number(i.unitPrice)||0)*(Number(i.quantity)||0);
  const totalReturn = returnItems.reduce((s,i)=>s+((Number(i.quantity)||0)>0 ? lineRefund(i) : 0),0);
  const itemsToReturn = returnItems.filter(i=>i.quantity>0);

  const handleSubmit = async () => {
    if (!selectedSale) return toast.error('Select a sale invoice first');
    if (itemsToReturn.length===0) return toast.error('Enter quantity for at least one item');
    setSaving(true);
    try {
      await saleReturnService.create({
        saleId: selectedSale._id,
        items:  itemsToReturn.map(i=>({
          medicine: i.medicine, quantity: i.quantity, unitPrice: i.unitPrice,
          refundAmount: lineRefund(i),
        })),
        refundMethod, reason, notes,
      });
      toast.success('Return processed — inventory restored');
      setFormOpen(false);
      setSelectedSale(null); setSaleSearch(''); setReturnItems([]);
      setReason(''); setNotes(''); setRefundMethod('cash');
      fetchData();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await saleReturnService.delete(deleteTarget._id);
      toast.success('Return cancelled, inventory reversed');
      setDeleteTarget(null);
      fetchData();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Sale Returns" subtitle="Manage medicine returns and refunds"
        actions={<Button onClick={()=>setFormOpen(true)}><Plus size={16}/> New Return</Button>}/>

      {/* Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={searchInput} onChange={e=>{
              const val = e.target.value;
              setSearchInput(val);
              clearTimeout(searchTimer.current);
              searchTimer.current = setTimeout(() => { setPage(1); setSearch(val); }, 350);
            }} placeholder="Search by return# or invoice#..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        {searchInput && <button onClick={()=>{ clearTimeout(searchTimer.current); setSearchInput(''); setSearch(''); }} className="text-gray-400 hover:text-gray-600"><X size={14}/></button>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Return #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Invoice #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Method</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(5).fill(0).map((_,i)=>(
              <tr key={i}><td colSpan={8} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>
            )) : data.length===0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">No returns found</td></tr>
            ) : data.map(row=>(
              <tr key={row._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs text-orange-700 font-medium">{row.returnNumber}</td>
                <td className="px-4 py-3 font-mono text-xs text-blue-700">{row.invoiceNumber||'-'}</td>
                <td className="px-4 py-3 text-gray-700">{row.customerName||'-'}</td>
                <td className="px-4 py-3 text-center text-gray-500">{row.items?.length}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-600">{formatCurrency(row.totalAmount)}</td>
                <td className="px-4 py-3 text-gray-500 capitalize">{row.refundMethod}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.createdAt)}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={()=>saleReturnService.getById(row._id).then(r=>setViewItem(r.data.data))}
                      className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"><Eye size={13}/></button>
                    <button onClick={()=>setDeleteTarget(row)}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total>20 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
            <div className="flex gap-1">
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Prev</button>
              <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* New Return Modal */}
      <Modal isOpen={formOpen} onClose={()=>{setFormOpen(false);setSelectedSale(null);setSaleSearch('');setReturnItems([]);}} title="New Sale Return" size="xl">
        <div className="space-y-4">
          {/* Step 1: Search by medicine name or invoice */}
          <FormField label="Search Medicine Name or Invoice Number" required>
            <div className="relative">
              <input value={saleSearch}
                onChange={e=>{setSaleSearch(e.target.value);searchSales(e.target.value);}}
                placeholder="Type medicine name or invoice number..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              {saleResults.length>0 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                  {saleResults.map(s=>(
                    <button key={s._id} type="button" onClick={()=>selectSale(s)}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0">
                      <p className="text-sm font-semibold text-blue-700">{s.invoiceNumber}</p>
                      <p className="text-xs text-gray-400">{s.customerName||'Walk-in'} · {toJalali(s.saleDate||s.createdAt)} · {formatCurrency(s.totalAmount)}</p>
                      {s.items?.some(i=>i.medicineName?.toLowerCase().includes(saleSearch.toLowerCase())) && (
                        <p className="text-xs text-green-600 mt-0.5">
                          Contains: {s.items.filter(i=>i.medicineName?.toLowerCase().includes(saleSearch.toLowerCase())).map(i=>i.medicineName).join(', ')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FormField>

          {/* Step 2: Select items to return */}
          {selectedSale && (
            <>
              <div className="bg-blue-50 rounded-xl p-3 text-sm flex gap-4">
                <div><span className="text-gray-400 text-xs">Invoice</span><p className="font-mono text-blue-700 font-bold">{selectedSale.invoiceNumber}</p></div>
                <div><span className="text-gray-400 text-xs">Customer</span><p>{selectedSale.customerName||'Walk-in'}</p></div>
                <div><span className="text-gray-400 text-xs">Date</span><p>{toJalali(selectedSale.saleDate||selectedSale.createdAt)}</p></div>
                <div><span className="text-gray-400 text-xs">Total</span><p className="font-semibold">{formatCurrency(selectedSale.totalAmount)}</p></div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Select Items to Return</p>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Sold Qty</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Return Qty</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Refund</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {returnItems.map((item, idx)=>(
                        <tr key={idx} className={item.quantity>0?'bg-orange-50':''}>
                          <td className="px-3 py-2 font-medium text-gray-800">{item.medicineName}</td>
                          <td className="px-3 py-2 text-center text-gray-500">{item.maxQty} {item.unit}</td>
                          <td className="px-3 py-2 text-center">
                            <input type="number" min="0" max={item.maxQty} value={item.quantity}
                              onChange={e=>updateQty(idx, e.target.value)}
                              className="w-20 text-center border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"/>
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" min="0" step="0.01" value={item.unitPrice}
                              onChange={e=>setReturnItems(prev=>prev.map((it,i)=>i===idx?{...it,unitPrice:Number(e.target.value),refundAmount:''}:it))}
                              className="w-24 text-right border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"/>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {item.quantity>0 ? (
                              <input type="number" min="0" step="0.01"
                                value={item.refundAmount}
                                placeholder={String(Number(item.unitPrice)*Number(item.quantity))}
                                onChange={e=>setReturnItems(prev=>prev.map((it,i)=>i===idx?{...it,refundAmount:e.target.value}:it))}
                                title="Refund amount for this item (leave empty for the full sale price)"
                                className="w-24 text-right border border-orange-300 rounded px-2 py-1 text-sm font-semibold text-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"/>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 font-semibold text-gray-600">Total Refund</td>
                        <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(totalReturn)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Refund Method">
                  <Select value={refundMethod} onChange={e=>setRefundMethod(e.target.value)}>
                    <option value="cash">Cash Refund</option>
                    <option value="credit">Credit (reduce debt)</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="mobile">Mobile Payment</option>
                  </Select>
                </FormField>
                <FormField label="Reason">
                  <Select value={reason} onChange={e=>setReason(e.target.value)}>
                    <option value="">Select reason</option>
                    <option value="damaged">Damaged / Defective</option>
                    <option value="wrong_medicine">Wrong Medicine</option>
                    <option value="expired">Expired</option>
                    <option value="patient_refused">Patient Refused</option>
                    <option value="other">Other</option>
                  </Select>
                </FormField>
                <div className="col-span-2">
                  <FormField label="Notes">
                    <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Additional notes..."/>
                  </FormField>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={()=>{setFormOpen(false);setSelectedSale(null);setSaleSearch('');setReturnItems([]);}}>Cancel</Button>
            <Button onClick={handleSubmit} loading={saving} disabled={itemsToReturn.length===0} className="bg-orange-600 hover:bg-orange-700 text-white">
              Process Return ({formatCurrency(totalReturn)})
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={!!viewItem} onClose={()=>setViewItem(null)} title={`Return — ${viewItem?.returnNumber}`} size="lg">
        {viewItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm bg-gray-50 rounded-xl p-3">
              <div><span className="text-xs text-gray-400">Return #</span><p className="font-mono text-orange-700 font-bold">{viewItem.returnNumber}</p></div>
              <div><span className="text-xs text-gray-400">Invoice #</span><p className="font-mono text-blue-700">{viewItem.invoiceNumber}</p></div>
              <div><span className="text-xs text-gray-400">Customer</span><p>{viewItem.customerName||'-'}</p></div>
              <div><span className="text-xs text-gray-400">Refund Method</span><p className="capitalize">{viewItem.refundMethod}</p></div>
              <div><span className="text-xs text-gray-400">Reason</span><p className="capitalize">{viewItem.reason||'-'}</p></div>
              <div><span className="text-xs text-gray-400">Date</span><p>{toJalali(viewItem.createdAt)}</p></div>
            </div>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {viewItem.items?.map((item,i)=>(
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium">{item.medicineName}</td>
                      <td className="px-3 py-2 text-center">{item.quantity} {item.unit}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-orange-600">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 font-semibold">Total Refund</td>
                    <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(viewItem.totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {viewItem.notes && <p className="text-xs text-gray-500">Notes: {viewItem.notes}</p>}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={()=>setViewItem(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDeleteTarget(null)}
        onConfirm={handleDelete} loading={deleting}
        title="Cancel Return"
        message={`Cancel return "${deleteTarget?.returnNumber}"? Inventory will be reversed.`}
        confirmLabel="Cancel Return"/>
    </div>
  );
}
